import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbItemClickEvent, BreadcrumbModule } from 'primeng/breadcrumb';

import { ConfigManagerService, CurrentSiteState, UrlService } from '@ppg/configuration';
import { API_BASE_URL } from '@ppg/core/constants';
import { ClientSiteSettingsKeys, UrlTarget } from '@ppg/core/enums';
import { LanguagesService } from '@ppg/core/language';
import { Breadcrumb } from '@ppg/core/models';

@Component({
  selector: 'ppg-breadcrumbs',
  standalone: true,
  imports: [BreadcrumbModule],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent {
  private readonly baseUrl = inject(API_BASE_URL);
  private readonly platform = inject(PLATFORM_ID);
  readonly #document = inject(DOCUMENT);
  private readonly configManagerService = inject(ConfigManagerService);
  private readonly currentSiteState = inject(CurrentSiteState);
  private readonly router = inject(Router);
  private readonly urlService = inject(UrlService);
  private readonly languagesService = inject(LanguagesService);

  readonly breadcrumbs = input.required<Breadcrumb[]>();
  readonly rootName = input<string>();
  readonly separator = input<string>('/');

  readonly showSiteRelatedBreadcrumbs = input<boolean>(true);

  readonly active = computed(() => {
    const key = ClientSiteSettingsKeys.FeatureBreadcrumbsIsActive;
    return this.currentSiteState.getSiteFlag(key);
  });
  readonly items = computed<MenuItem[]>(() => this.setBreadcrumbs(this.breadcrumbs()));
  readonly #locationProtocol = computed(() =>
    isPlatformBrowser(this.platform) ? this.#document.location.protocol : '',
  );

  setBreadcrumbs(breadcrumbs: Breadcrumb[]): MenuItem[] {
    const rootSite = this.configManagerService.rootSite();
    const currentSite = this.currentSiteState.currentSite();
    const language = this.languagesService.language();
    const isHubPage = !this.urlService.getSlug(this.router.url);
    const isRootSite = this.currentSiteState.currentSite()?.isRootSite;
    const isCurrentSiteUnderRootDomain = this.configManagerService.isCurrentSiteUnderRootDomain();

    let rootSiteUrlSegment: MenuItem[] = [];
    let buSiteUrlSegment: MenuItem[] = [];

    if (!breadcrumbs.length || isHubPage) {
      return [];
    }

    const menuItems = breadcrumbs.map((item) => ({
      label: item.title,
      routerLink: item.url,
      dataTestId: item.dataTestId,
      queryParams: item.queryParams,
    }));

    if (rootSite?.nameInBreadcrumbs && this.showSiteRelatedBreadcrumbs()) {
      if (!isRootSite && !isCurrentSiteUnderRootDomain) {
        rootSiteUrlSegment = [
          {
            label: this.rootName() ?? rootSite.nameInBreadcrumbs,
            url: `${this.#locationProtocol()}//${this.configManagerService.getRootSiteDefaultDomainName()}/${language}`,
            target: UrlTarget.SELF,
          },
        ];
      } else {
        rootSiteUrlSegment = [
          {
            label: this.rootName() ?? rootSite.nameInBreadcrumbs,
            routerLink: `${this.baseUrl}${language}`,
          },
        ];
      }
      rootSiteUrlSegment[0]['dataTestId'] = 'breadcrumbs.homeElement';
    }

    if (
      currentSite &&
      !currentSite.isRootSite &&
      currentSite.nameInBreadcrumbs &&
      currentSite.rootName &&
      this.showSiteRelatedBreadcrumbs()
    ) {
      if (!isRootSite && !isCurrentSiteUnderRootDomain) {
        buSiteUrlSegment = [
          {
            label: currentSite.nameInBreadcrumbs,
            routerLink: `${this.baseUrl}${language}`,
          },
        ];
      } else {
        buSiteUrlSegment = [
          {
            label: currentSite.nameInBreadcrumbs,
            routerLink: `${this.baseUrl}${currentSite.rootName}/${language}`,
          },
        ];
      }
    }

    return [...rootSiteUrlSegment, ...buSiteUrlSegment, ...menuItems];
  }

  onItemClick(event: BreadcrumbItemClickEvent) {
    this.breadcrumbs()
      .find((breadcrumb) => breadcrumb.url === event.item.routerLink)
      ?.clickHandler?.();
  }
}
